/**
 * Heights-parity button fill wave (WAAPI) — mirrors their hover-button
 * component, measured live on theheightsdistrict.com 2026-09-11 by
 * rAF-sampling the product add-to-cart .btn-fill transform:
 *
 * - ENTER: fill jumps instantly to translate 0 76% (below the button) and
 *   rises to 0 over 0.6s ease. Re-enter ALWAYS restarts from below, even
 *   while an exit is mid-flight (measured: exit at -77.8% -> jump to +91.2px
 *   -> full rise).
 * - LEAVE: fill continues upward from its CURRENT position to -76% over
 *   0.6s ease — the receding-wave phase. Measured mid-enter leave: rise at
 *   +25.1px reversed into a smooth exit from exactly there. Never snaps.
 *
 * A CSS hover-state keyframe animation cannot do this: removing the
 * animation on unhover discards the in-flight value, which is why the wave
 * used to vanish without receding on quick pointer exits.
 *
 * Geometry + colors live in assets/base.css (signature effects block); this
 * module only injects the .btn-fill span (Heights ships it in markup) and
 * drives translate. Gated like the CSS: body[data-signature-effects],
 * reduced-motion off, mouse pointers only — never focus (see the note at
 * the bottom).
 */
(() => {
  const EXCLUDE =
    '.button-unstyled, .button--unstyled, .shopify-payment-button__button--unbranded, ' +
    '.close-button, .menu-drawer__back-button, .cart-items__remove, .variant-item__remove, ' +
    '.dropdown-localization__button, .predictive-search__close-modal-button, [data-no-fill]';
  const SELECTOR = `.button:not(${EXCLUDE}), .button--fill, .button-custom:not([data-no-fill])`;
  const DURATION = 600;
  const EASING = 'ease';
  const PARKED_ABOVE = '0 -76%';
  const PARKED_BELOW = '0 76%';
  const COVERING = '0 0';

  const running = new WeakMap();
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  const enabled = () => document.body.hasAttribute('data-signature-effects') && !reducedMotion.matches;

  function fillOf(button, create) {
    let fill = button.querySelector(':scope > .btn-fill');
    if (!fill && create) {
      fill = document.createElement('span');
      fill.className = 'btn-fill';
      fill.setAttribute('aria-hidden', 'true');
      button.prepend(fill);
    }
    return fill;
  }

  function play(fill, from, to) {
    running.get(fill)?.cancel();
    const animation = fill.animate([{ translate: from }, { translate: to }], {
      duration: DURATION,
      easing: EASING,
      fill: 'forwards',
    });
    running.set(fill, animation);
  }

  /* One mouse = at most one waved button. Tracking the active button lets
     ANY pointerover outside it park the wave — this is what survives the
     quick-add modal: dialog.showModal() makes the page behind it inert, so
     the browser NEVER dispatches pointerout to the clicked pill (measured
     live: fill froze at 0% covering = the stuck-white "Choose options"
     pill). The next pointer move lands on modal content, which is not
     inert, and the safety branch below parks the pill behind it. */
  let activeButton = null;

  /* Linked groups (`data-fill-link="<group>"` on sibling buttons): entering
     one button also waves its partners — same enter-from-below / recede-
     from-current motion, own colors — and `data-fill-linked` on the partner
     drives the CSS label flip the way :hover does on the active button.
     Cart drawer "Check out" / "View Cart" pair (user request 2026-09-24). */
  let activeLinked = [];

  const disabled = (button) => button.matches('[disabled], [aria-disabled="true"]');

  function linkedOf(button) {
    const group = button.dataset.fillLink;
    if (!group) return [];
    const scope = button.parentElement ?? document;
    return [...scope.querySelectorAll(`[data-fill-link="${CSS.escape(group)}"]`)].filter(
      (partner) => partner !== button && partner.matches(SELECTOR) && !disabled(partner)
    );
  }

  /* Whether the linked group's fills are currently covering. A partner
     switch toggles this with ONE motion (see switchTo). */
  let groupCovered = false;

  function cover(button) {
    groupCovered = true;
    button.removeAttribute('data-fill-rest');
    play(fillOf(button, true), PARKED_BELOW, COVERING);
    for (const partner of activeLinked) {
      partner.setAttribute('data-fill-linked', '');
      play(fillOf(partner, true), PARKED_BELOW, COVERING);
    }
  }

  function enter(button) {
    // Heights hides the fill on disabled buttons (.button[disabled] .btn-fill).
    if (disabled(button)) return;
    if (activeButton && activeButton !== button) park(activeButton);
    activeButton = button;
    activeLinked = linkedOf(button);
    cover(button);
  }

  function recede(button) {
    const fill = fillOf(button, false);
    if (!fill) return;
    const current = getComputedStyle(fill).translate;
    play(fill, current === 'none' ? COVERING : current, PARKED_ABOVE);
  }

  function park(button) {
    if (activeButton === button) {
      activeButton = null;
      groupCovered = false;
      button.removeAttribute('data-fill-rest');
      for (const partner of activeLinked) {
        partner.removeAttribute('data-fill-linked');
        recede(partner);
      }
      activeLinked = [];
    }
    recede(button);
  }

  /* Moving the mouse from one linked partner to another (8px gap in the cart
     drawer) plays exactly ONE wave at the normal speed, alternating: a
     covered group RECEDES (Check out back to ink/white label, View Cart back
     to white/ink — `data-fill-rest` holds the hovered label at rest over
     the uncovered button), an uncovered group RISES again. Leaving an
     uncovered group changes nothing; re-entering from outside always rises.
     Crossing the gap gets a short grace so it counts as a switch, not a
     leave. (User-tuned 2026-09-24: recede-then-rise read as "two effects",
     an instant restart as "too fast", a silent handover as "no effect".) */
  const GAP_GRACE = 80;
  let pendingPark = 0;

  function clearPendingPark() {
    if (!pendingPark) return;
    clearTimeout(pendingPark);
    pendingPark = 0;
  }

  function switchTo(button) {
    activeButton = button;
    activeLinked = linkedOf(button);
    const group = [button, ...activeLinked];
    for (const member of group) {
      member.removeAttribute('data-fill-linked');
      member.removeAttribute('data-fill-rest');
    }
    if (!groupCovered) {
      cover(button);
      return;
    }
    groupCovered = false;
    button.setAttribute('data-fill-rest', '');
    for (const member of group) recede(member);
  }

  function parkActive() {
    clearPendingPark();
    if (activeButton) park(activeButton);
  }

  const buttonFrom = (event) =>
    event.target instanceof Element ? event.target.closest(SELECTOR) : null;

  document.addEventListener(
    'pointerover',
    (event) => {
      if (event.pointerType !== 'mouse' || !enabled()) return;
      const button = buttonFrom(event);
      if (button) {
        if (button === activeButton) return;
        clearPendingPark();
        if (activeLinked.includes(button)) switchTo(button);
        else enter(button);
      } else if (activeButton && !pendingPark) {
        // Pointer surfaced somewhere else without a pointerout on the active
        // button (inert page under a modal, hidden/faded pill, occlusion).
        park(activeButton);
      }
    },
    true
  );

  document.addEventListener(
    'pointerout',
    (event) => {
      if (event.pointerType !== 'mouse' || !enabled()) return;
      const button = buttonFrom(event);
      // Only the active button parks here; a second park mid-recede would
      // restart the exit timing from the current position.
      if (!button || button !== activeButton || button.contains(event.relatedTarget)) return;
      const target = event.relatedTarget;
      if (activeLinked.length && target instanceof Element && button.parentElement?.contains(target)) {
        // Landed on a partner: its pointerover hands over. Landed in the gap
        // between partners: give the partner a beat to claim the wave.
        if (activeLinked.some((partner) => partner.contains(target))) return;
        clearPendingPark();
        pendingPark = setTimeout(() => {
          pendingPark = 0;
          if (activeButton === button) park(button);
        }, GAP_GRACE);
        return;
      }
      park(button);
    },
    true
  );

  document.addEventListener('pointercancel', parkActive);

  window.addEventListener('blur', parkActive);

  /* No focus handling on purpose (Heights-exact): their fill is
     mouse-only and :focus-visible shows the outline with resting colors.
     Driving the wave from focus made every quick-add modal close re-cover
     the pill white — dialog close restores focus to the invoker as
     :focus-visible. The CSS text flip is :hover-only for the same reason. */
})();
